import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, Hammer, LogIn, MessageSquare, ChevronDown, Heart, LogOut, Settings, Bell } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut } = useAuth();
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  
  const isActive = (path: string) => location.pathname === path;
  
  const handleLogout = () => {
    signOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Find værktøj', path: '/browse' },
  ];
  const avatarSrc = user?.avatarImageUrl || user?.avatar;
  const ownProfilePath = user?.id ? `/profile/${user.id}` : '/profile';

  return (
    <header className="bg-[#f5f0e8] shadow-sm sticky top-0 z-50 transition-all duration-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="DoMore" className="h-10" />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${
                  isActive(link.path)
                    ? 'text-[#1F7A78] font-medium'
                    : 'text-gray-600 hover:text-[#1F7A78]'
                } transition-colors duration-200`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          {/* Auth Buttons - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard?tab=tools" className="text-gray-600 hover:text-[#1F7A78]">
                  <Hammer className="h-5 w-5" />
                </Link>

                {/* User Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center space-x-2 text-gray-600 hover:text-[#1F7A78] focus:outline-none"
                  >
                    <div className="h-8 w-8 rounded-full bg-[#1F7A78]/10 flex items-center justify-center text-[#1F7A78] font-medium overflow-hidden">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt={user?.name} className="h-full w-full object-cover" />
                      ) : (
                        <span>{user?.name?.charAt(0).toUpperCase() || "U"}</span>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      
                      <Link
                        to={ownProfilePath}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4 mr-3" />
                        Profil
                      </Link>
                      
                      <Link
                        to="/dashboard?tab=messages"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <MessageSquare className="h-4 w-4 mr-3" />
                        Beskeder
                      </Link>

                      <Link
                        to="/notifications"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Bell className="h-4 w-4 mr-3" />
                        Notifikationer
                      </Link>
                      
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Indstillinger
                      </Link>
                      
                      <Link
                        to="/favorites"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Heart className="h-4 w-4 mr-3" />
                        Favoritter
                      </Link>
                      
                      <div className="border-t border-gray-100">
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Log Ud
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/signin">
                  <Button 
                    variant="outline" 
                    icon={<LogIn className="h-4 w-4 mr-2" />}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Log ind
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button 
                    variant="primary"
                    className="bg-[#1F7A78] text-white hover:bg-[#1F7A78]/90"
                  >
                    Opret konto
                  </Button>
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-600"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#f5f0e8] border-t border-gray-200 py-2">
          <div className="container mx-auto px-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${
                  isActive(link.path)
                    ? 'text-[#1F7A78] font-medium'
                    : 'text-gray-600'
                } block py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="px-3 py-3 bg-white/50 rounded-md mb-3 border border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>

                  <Link 
                    to="/dashboard?tab=tools"
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Hammer className="h-5 w-5" />
                    <span>Mit værktøj</span>
                  </Link>
                  
                  <Link 
                    to={ownProfilePath}
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-5 w-5" />
                    <span>Profil</span>
                  </Link>
                  
                  <Link 
                    to="/dashboard?tab=messages" 
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <MessageSquare className="h-5 w-5" />
                    <span>Beskeder</span>
                  </Link>

                  <Link 
                    to="/notifications" 
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Bell className="h-5 w-5" />
                    <span>Notifikationer</span>
                  </Link>
                  
                  <Link 
                    to="/settings" 
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Settings className="h-5 w-5" />
                    <span>Indstillinger</span>
                  </Link>
                  
                  <Link 
                    to="/favorites" 
                    className="flex items-center space-x-2 text-gray-700 py-2 px-2 rounded-md hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Heart className="h-5 w-5" />
                    <span>Favoritter</span>
                  </Link>
                  
                  <div className="border-t border-gray-200 mt-2 pt-2">
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-2 w-full text-red-600 py-2 px-2 rounded-md hover:bg-red-50"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Log Ud</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="pt-2 space-y-2">
                <Link to="/signin" onClick={() => setIsMenuOpen(false)}>
                  <Button 
                    fullWidth 
                    variant="outline" 
                    icon={<LogIn className="h-4 w-4 mr-2" />}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Log ind
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                  <Button 
                    fullWidth 
                    variant="primary"
                    className="bg-[#1F7A78] text-white hover:bg-[#1F7A78]/90"
                  >
                    Opret konto
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;